/**
 * The structural moment — fetch and record (Relationship Detection Assist PRD
 * v2, phase R2).
 *
 * Gate model and arithmetic: src/lib/structuralValidationCore.ts.
 * Canon: docs/1-canon/12-item-relationship-taxonomy-and-mutation-rules.md §14.
 *
 * WHAT THIS MODULE MAY DO. Read the two detectors' open output for a pool, and
 * append one record of what a room decided. That is all.
 *
 * WHAT IT MAY NOT DO, AND WHY IT HAS NO CODE FOR IT. It never accepts,
 * dismisses, merges or creates a relationship. Canon 12 §14.3: "No
 * relationship record is created, modified, or deleted by the Nexus agent. All
 * authority transitions require explicit human action." Those transitions have
 * exactly one implementation each and this module calls them rather than
 * reimplementing them:
 *
 *   cluster identity → accept_twins_cluster / dismiss_twins_cluster (mig 371)
 *                      via twinsAgentService.acceptCluster / dismissCluster
 *   structure        → accept_nexus_proposal / dismiss_nexus_proposal (mig 462)
 *                      via nexusAgentService.acceptNexusProposal / dismissNexusProposal
 *
 * THE VALIDATION RECORD NEEDS NO MIGRATION. `session_events` (mig 311) is
 * append-only, workspace-scoped by RLS, and already carries `actor_id`,
 * `actor_name`, `event_type` and a free `payload` — which is precisely the
 * shape of "who validated this set, when, and what they found". Putting it in
 * `mode_config.v3Config` instead would have been wrong twice over: that blob is
 * rewritten wholesale on every step edit (see store.ts `syncV3Config`), so a
 * governance record living there is one careless write away from gone, and it
 * holds current state rather than history — a session validated twice would
 * keep only the second answer.
 */

import { getSupabaseClient } from './supabaseClient';
import { emitSessionEvent } from './sessionEvents';
import { getActiveClusters } from './twinsAgentService';
import { getAgentSettings } from './agentSettingsService';
import { getNexusProposals } from './nexusAgentService';
import {
  buildStructuralValidation,
  type ClusterProposalRecord,
  type Resolution,
  type StructuralProposalRecord,
  type StructuralValidationReport,
  type ViolationRecord,
} from './structuralValidationCore';

/** The `session_events.event_type` this phase writes. */
export const STRUCTURAL_VALIDATION_EVENT = 'comparison_set_validated';

export interface DetectorOutput {
  clusters: ClusterProposalRecord[];
  proposals: StructuralProposalRecord[];
  error: string | null;
  /**
   * Whether each agent is actually configured and enabled for this workspace.
   *
   * WHY THIS IS READ (defect, 2026-09-02). Both fetches return open proposals.
   * A workspace that has never configured Twins or Nexus returns zero of them —
   * byte-identical to a set those agents examined and passed. The panel then
   * reported "nothing found", which is an all-clear the product had not earned,
   * on the one screen whose entire job is refusing unearned confidence. The
   * agent config row is the honest signal: no row, or `enabled: false`, means
   * that gate had nobody looking.
   */
  twinsEnabled: boolean;
  nexusEnabled: boolean;
}

const EMPTY: DetectorOutput = {
  clusters: [],
  proposals: [],
  error: null,
  twinsEnabled: false,
  nexusEnabled: false,
};

/**
 * Read every open question the two agents have about the items in this pool.
 *
 * Both fetches are workspace-wide because that is what the existing services
 * expose; the pool narrowing happens in the pure core, which is also where it
 * is tested. Filtering here as well would put the same rule in two places.
 */
export async function fetchDetectorOutputForPool(
  workspaceId: string,
  poolItemIds: string[],
): Promise<DetectorOutput> {
  if (poolItemIds.length === 0) return EMPTY;

  const [clusterRes, proposalRes, twinsCfg, nexusCfg] = await Promise.all([
    getActiveClusters(workspaceId),
    getNexusProposals(workspaceId, 'active'),
    getAgentSettings(workspaceId, 'twins'),
    getAgentSettings(workspaceId, 'nexus'),
  ]);

  const error = clusterRes.error ?? proposalRes.error ?? null;

  const clusters: ClusterProposalRecord[] = clusterRes.data.map((c) => ({
    id: c.id,
    clusterType: c.clusterType,
    itemIds: c.itemIds,
    rationale: c.rationale,
    status: c.status,
    proposedByAgent: c.proposedByAgent,
  }));

  const proposals: StructuralProposalRecord[] = proposalRes.data.map((p) => ({
    id: p.id,
    sourceItemId: p.sourceItemId,
    targetItemId: p.targetItemId,
    relationshipType: p.relationshipType,
    relationshipSubtype: p.relationshipSubtype,
    rationale: p.rationale,
    confidence: p.confidence,
    status: p.status,
    proposedByAgent: p.proposedByAgent,
  }));

  return {
    clusters,
    proposals,
    error,
    twinsEnabled: twinsCfg.data?.enabled === true,
    nexusEnabled: nexusCfg.data?.enabled === true,
  };
}

// ---------------------------------------------------------------------------
// The validation record
// ---------------------------------------------------------------------------

/**
 * What one recorded validation carries. Findings are stored resolved-and-all,
 * with their dispositions and override reasons, so the record answers "what
 * was in front of the room" and not merely "what they cleared".
 */
export interface StructuralValidationRecordPayload {
  poolSize: number;
  readiness: StructuralValidationReport['readiness'];
  identityResolved: boolean;
  recordLine: string;
  totals: StructuralValidationReport['totals'];
  dispositionCounts: StructuralValidationReport['dispositionCounts'];
  /** One entry per finding that was in front of the room. */
  findings: Array<{
    id: string;
    gate: string;
    disposition: 'block' | 'warn';
    detail: string;
    source: string;
    itemIds: string[];
    deferred: boolean;
    resolution?: Resolution;
  }>;
  /** Items in the set at validation time — what the counts refer to. */
  poolItemIds: string[];
}

export interface RecordedStructuralValidation extends StructuralValidationRecordPayload {
  actorId: string;
  actorName: string;
  validatedAt: string;
}

/** Flatten a report into the payload the event carries. */
export function toRecordPayload(
  report: StructuralValidationReport,
  poolItemIds: string[],
): StructuralValidationRecordPayload {
  return {
    poolSize: report.poolSize,
    readiness: report.readiness,
    identityResolved: report.identityResolved,
    recordLine: report.recordLine,
    totals: report.totals,
    dispositionCounts: report.dispositionCounts,
    findings: report.gates.flatMap((g) =>
      g.findings.map((f) => ({
        id: f.id,
        gate: f.gate,
        disposition: f.disposition,
        detail: f.detail,
        source: f.source,
        itemIds: f.itemIds,
        deferred: f.deferred,
        resolution: f.resolution,
      })),
    ),
    poolItemIds,
  };
}

/**
 * Append the validation record for a session.
 *
 * Append-only by construction: a re-validation writes a second event rather
 * than editing the first, so "we validated, then added four items and
 * validated again" stays legible. The reader below takes the latest.
 */
export async function recordStructuralValidation(
  sessionId: string,
  actorId: string,
  actorName: string,
  report: StructuralValidationReport,
  poolItemIds: string[],
): Promise<{ success: boolean; error?: string }> {
  return emitSessionEvent(
    sessionId,
    actorId,
    actorName,
    STRUCTURAL_VALIDATION_EVENT,
    toRecordPayload(report, poolItemIds) as unknown as Record<string, unknown>,
  );
}

/**
 * The most recent validation record for a session, or null.
 *
 * Read-only. Used by the panel (to show what was already recorded) and by any
 * downstream surface that wants the record line — the catchball report being
 * the one the PRD names.
 */
export async function fetchLatestStructuralValidation(
  sessionId: string,
): Promise<{ data: RecordedStructuralValidation | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('session_events')
      .select('actor_id, actor_name, payload, created_at')
      .eq('session_id', sessionId)
      .eq('event_type', STRUCTURAL_VALIDATION_EVENT)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) return { data: null, error: error.message };
    if (!data) return { data: null, error: null };

    const row = data as {
      actor_id: string;
      actor_name: string;
      payload: unknown;
      created_at: string;
    };
    const payload = (row.payload ?? {}) as StructuralValidationRecordPayload;

    return {
      data: {
        ...payload,
        actorId: row.actor_id,
        actorName: row.actor_name,
        validatedAt: row.created_at,
      },
      error: null,
    };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Build the report for a pool in one call — fetch the detectors, fold in the
 * anti-pattern violations the caller already computed, apply resolutions.
 *
 * `violations` is a parameter rather than another fetch because
 * `computeViolations` runs off the relationship graph the client already holds
 * in `itemRelationshipsStore`; re-querying it here would be a second, divergent
 * load of data that is already in memory.
 */
export async function buildPoolStructuralValidation(
  workspaceId: string,
  poolItemIds: string[],
  violations: ViolationRecord[],
  resolutions: Record<string, Resolution>,
): Promise<{ report: StructuralValidationReport; error: string | null }> {
  const { clusters, proposals, error, twinsEnabled, nexusEnabled } =
    await fetchDetectorOutputForPool(workspaceId, poolItemIds);
  return {
    report: buildStructuralValidation({
      poolItemIds,
      clusters,
      proposals,
      violations,
      resolutions,
      checkedGates: { identity: twinsEnabled, structure: nexusEnabled },
    }),
    error,
  };
}
